/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { CompletionStatus, FetchType, SkillFrequency } from '../../../enums';
import { LearnerSelfReviews, LearnerSkillResource } from '../../../models/learner-skill-resource';
import { useAppActions, useAppState } from '../../../overmind';
import { RatingData } from '../../../components/inputs/raters/raters.models';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { Button, ListLayout, PageHeader } from '@keplerco/core';
import { RaterCard } from '../../../components/inputs/raters/rater-card/rater-card';
import { useAnalysisBasePagePath } from '../use-analysis-base-page-path';
import { RatingScale } from '../../../models/rating-scale';
import { useKeplerNavigate } from '../../../navigation/guards/use-kepler-navigate';

export function FrequencyOfUsePage(): JSX.Element {
  const actions = useAppActions();
  const keplerNavigate = useKeplerNavigate();
  const { activeAssessmentConfig, forcedAssessments } = useAppState();
  const basePagePath = useAnalysisBasePagePath();

  const [data, setData] = useState<LearnerSelfReviews>();
  const bgColor = getComputedStyle(document.body).backgroundColor;

  useEffect(() => {
    async function getData() {
      actions.startLoader({ path: PagePath.analysisFrequencyOfUse, type: FetchType.PageFetching });

      const tempData = await actions.getSelfReviews();
      setData(tempData);

      actions.stopLoader(PagePath.analysisFrequencyOfUse);
    }

    getData();
  }, []);

  async function onSubmitHandler() {
    actions.startLoader({ path: PagePath.analysisFrequencyOfUse, type: FetchType.Sending });

    await actions.submitSelfReviews(data!);

    // @Jedidja
    const skillAssessmentConfiguration = await actions.getAssessmentConfig(activeAssessmentConfig!.assessmentSlug);

    if (skillAssessmentConfiguration?.selfReviewCompletionStatus === CompletionStatus.Completed && activeAssessmentConfig?.selfPeerReviewCompletionStatus === CompletionStatus.NoActionRequired) {
      actions.clearAssessmentConfig();
    }

    keplerNavigate(`${basePagePath}${PagePath.analysisSelfAssessment}`);
    window.scrollTo(0, 0);

    actions.stopLoader(PagePath.analysisFrequencyOfUse);
  }

  const handleRatingChange = (skill: LearnerSkillResource, newRating: number) => {
    setData(prevData => {
      if (!prevData) return prevData;

      return {
        ...prevData,
        reviews: prevData.reviews.map(item => (item.companySkillId === skill.companySkillId ? { ...item, skillFrequency: newRating } : item)),
      };
    });
  };

  const scale: RatingScale = [
    { label: 'Never', rating: SkillFrequency.Never, description: 'Never Used' },
    { label: 'Daily', rating: SkillFrequency.Daily, description: 'Used Daily' },
    { label: 'Weekly', rating: SkillFrequency.Weekly, description: 'Weekly' },
    { label: 'Monthly', rating: SkillFrequency.Monthly, description: 'Monthly' },
  ];

  const allSelected = !!data && data.reviews.every(review => review.skillFrequency !== null);

  return (
    <PageLoaderLayer path={PagePath.analysisFrequencyOfUse}>
      <div className="wrapper" style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
        <PageHeader
          breadcrumbs={
            forcedAssessments.length > 1
              ? [
                  {
                    name: 'Pending assessments',
                    url: PagePath.analysisBase + PagePath.analysisPendingAssessments,
                  },
                  {
                    name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                    url: basePagePath,
                  },
                  {
                    name: 'Frequency of use',
                    url: location.pathname,
                  },
                ]
              : [
                  {
                    name: activeAssessmentConfig?.assessmentName ?? 'Assessment',
                    url: basePagePath,
                  },
                  {
                    name: 'Frequency of use',
                    url: location.pathname,
                  },
                ]
          }
          title="Frequency of use"
          subtitle="Confirm how often you use the following skills. Knowing how often a skill is used helps reveal the link between practice and performance."
          divider
        />

        <div style={{ overflowY: 'auto', width: '100%', maxHeight: 'calc(100vh - 375px)' }}>
          <ListLayout>
            {data?.reviews.map(review => {
              const tempRatingData: RatingData = {
                id: review.companySkillId,
                label: review.skillName,
                description: review.skillDescription,
                rating: review.skillFrequency,
              };

              return (
                <li key={review.companySkillId} id={`rater-${review.companySkillId}`}>
                  <RaterCard bgColor={bgColor} onChange={newRating => handleRatingChange(review, newRating)} data={tempRatingData} defaultValue={review.skillFrequency} scale={scale} colour="link-text" />
                </li>
              );
            })}
          </ListLayout>
        </div>

        <footer style={{ height: 50, alignSelf: 'flex-end' }}>
          <Button type="button" disabled={!allSelected} onClick={() => allSelected && onSubmitHandler()}>
            Submit
          </Button>
        </footer>
      </div>
    </PageLoaderLayer>
  );
}
